import { memo } from 'react';
import { motion } from 'framer-motion';

import { SLICE_PER_BREAD } from '../../../constants/currencies';
import { LightSwipe } from '../LightSwipe';
import { PngIcon } from '../PngIcon';

import styles from './BreadBar.module.css';

export type BreadBarProps = {
  breadSlice: number;
  height?: string;
  width?: string;
  iconSize?: number;
  backgroundColor?: string;
  isAnimating?: boolean;
  breadPosRatio?: number;
  levelFontsizeRatio?: number;
};

const slicePerBread = SLICE_PER_BREAD;

export const BreadBar = memo(
  ({
    breadSlice,
    height = '16px',
    width = '100%',
    iconSize,
    backgroundColor = '#F0F2F5',
    isAnimating = false,
    breadPosRatio = 3,
    levelFontsizeRatio = 2.5,
  }: BreadBarProps) => {
    const fillColor = '#EB8C36';
    const textColor = '#FFF';

    const slice = breadSlice % slicePerBread;
    const bread = (breadSlice - slice) / slicePerBread;
    const precentage = (slice / slicePerBread) * 100;

    return (
      <div className={styles.breadBar}>
        {iconSize !== undefined ? (
          <>
            <motion.div
              className={styles.icon}
              style={{ marginRight: `${-iconSize / 8}px` }}
              animate={{ scale: isAnimating ? 1.3 : 1 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <PngIcon name={'Bread'} width={iconSize} height={iconSize} />
            </motion.div>
            <div
              className={styles.level}
              style={{
                position: 'absolute',
                left: (iconSize * 2) / breadPosRatio,
                bottom: 0,
                fontSize: iconSize / levelFontsizeRatio,
              }}
            >
              {bread}
            </div>
          </>
        ) : (
          <div
            className={styles.level}
            style={{ fontSize: height, marginRight: `calc(-${height} / 3)` }}
          >
            {bread}
          </div>
        )}
        <div className={styles.progressBar} style={{ width, backgroundColor }}>
          <motion.div
            key={bread}
            style={{
              width: `${precentage}%`,
              backgroundColor: fillColor,
              height,
            }}
            animate={{ width: `${precentage}%` }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <LightSwipe />
            <span
              className={styles.progressText}
              style={{
                height,
                color: textColor,
                lineHeight: height,
                fontSize: `calc(${height} * 0.8)`,
                WebkitTextStrokeWidth: `calc(${height} / 5)`,
                WebkitTextStrokeColor: fillColor,
                paintOrder: 'stroke fill',
              }}
            >
              {`${slice}/${slicePerBread}`}
            </span>
          </motion.div>
        </div>
      </div>
    );
  },
);
